// src/Components/HelpFeedbackWidget.js
// ─────────────────────────────────────────────────────────────────────────
//  Floating "Help" button (bottom-right) shown on every page.
//  Clicking it slides open a right-side sidebar (not a small popup).
//  User can:
//    - Auto-capture a screenshot of the current page, OR
//    - Manually upload a screenshot, OR
//    - Just type a text description
//  Clicking "Send" always opens Web WhatsApp (web.whatsapp.com) directly,
//  pre-filled with the message text to 9486042369.
//
//  Requires: npm install html2canvas
//  Also requires: src/assets/help-robot.png (the robot mascot image)
// ─────────────────────────────────────────────────────────────────────────
import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import helpRobotImg from '../assets/help-robot.png';

const WHATSAPP_NUMBER = '919486042369'; // 91 = India country code + number

export default function HelpFeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [screenshotBlob, setScreenshotBlob] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const fileInputRef = useRef(null);

  const resetPanel = () => {
    setDescription('');
    setScreenshotBlob(null);
    setScreenshotPreview(null);
    setStatusMsg('');
  };

  const toggleOpen = () => {
    setOpen((o) => !o);
    if (open) resetPanel();
  };

  const captureScreenshot = async () => {
    setCapturing(true);
    setStatusMsg('');
    try {
      const widgetEl = document.getElementById('help-feedback-widget-root');
      if (widgetEl) widgetEl.style.visibility = 'hidden';

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: -window.scrollY,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
      });

      if (widgetEl) widgetEl.style.visibility = 'visible';

      canvas.toBlob((blob) => {
        if (blob) {
          setScreenshotBlob(blob);
          setScreenshotPreview(URL.createObjectURL(blob));
        } else {
          setStatusMsg('Screenshot capture failed. Try again.');
        }
        setCapturing(false);
      }, 'image/png');
    } catch (err) {
      console.error('Screenshot capture error:', err);
      setStatusMsg('Could not capture screenshot: ' + err.message);
      setCapturing(false);
    }
  };

  const handleManualUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScreenshotBlob(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const removeScreenshot = () => {
    setScreenshotBlob(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const buildMessageText = () => {
    const pageUrl = window.location.href;
    const desc = description.trim() || '(No description entered)';
    return (
      `*Help / Issue Report*\n\n` +
      `Page: ${pageUrl}\n` +
      `Description: ${desc}\n` +
      (screenshotBlob
        ? `\n(Screenshot attached — please check the shared image / the file just downloaded to your device)`
        : '')
    );
  };

  const handleSend = async () => {
    setSending(true);
    setStatusMsg('');
    const messageText = buildMessageText();

    try {
      if (screenshotBlob) {
        const file = new File(
          [screenshotBlob],
          `issue-screenshot-${Date.now()}.png`,
          { type: screenshotBlob.type || 'image/png' }
        );

        const downloadUrl = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);

        setStatusMsg('Screenshot downloaded. Opening Web WhatsApp — please attach it there.');
      } else {
        setStatusMsg('Opening Web WhatsApp...');
      }

      const waUrl = `https://web.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(messageText)}`;
      window.open(waUrl, '_blank', 'noopener,noreferrer');

      setSending(false);
      setOpen(false);
      resetPanel();
    } catch (err) {
      console.error('Send error:', err);
      setStatusMsg('Something went wrong: ' + err.message);
      setSending(false);
    }
  };

  return (
    <div id="help-feedback-widget-root">
      <style>{`
        @keyframes hfw-jump {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-10px); }
        }
      `}</style>

      {/* Floating Help button - Only show when panel is CLOSED */}
      {!open && (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 10001 }}>
          {/* "Help" text on top of robot image - with JUMPING animation */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '4px',
            fontSize: '14px',
            fontWeight: 800,
            color: '#DC2626',  // Red text
            fontFamily: 'Segoe UI, Arial, sans-serif',
            textShadow: '0 1px 2px rgba(255,255,255,0.8)',
            letterSpacing: '0.5px',
            animation: 'hfw-jump 1.2s ease-in-out infinite',
          }}>
            Help
          </div>
          
          {/* Robot Image Button - NO red circle background */}
          <button
            onClick={toggleOpen}
            aria-label="Get help"
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'transparent',  // No red background
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              padding: '0',
              transition: 'transform 0.15s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.06)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          >
            <img
              src={helpRobotImg}
              alt="Help"
              style={{ 
                width: '64px', 
                height: '64px', 
                objectFit: 'cover', 
                objectPosition: 'center 30%',
                borderRadius: '50%'
              }}
            />
          </button>
        </div>
      )}

      {/* Backdrop overlay behind the sidebar */}
      {open && (
        <div
          onClick={toggleOpen}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(11,31,77,0.35)',
            zIndex: 9998,
          }}
        />
      )}

      {/* Sidebar panel — slides in from the right, full height */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: '360px',
          maxWidth: '90vw',
          background: '#fff',
          borderLeft: '1px solid #BAE0FA',
          boxShadow: '-12px 0 32px rgba(11,31,77,0.2)',
          zIndex: 9999,
          fontFamily: 'Segoe UI, Arial, sans-serif',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.28s ease',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '18px 20px', borderBottom: '1px solid #E5EEFB',
            background: 'linear-gradient(135deg, #1E3A8A, #0B1F4D)', flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img 
              src={helpRobotImg} 
              alt="" 
              style={{ 
                width: 36, 
                height: 36, 
                borderRadius: '50%', 
                objectFit: 'cover', 
                objectPosition: 'center 30%',
                border: '2px solid #BAE0FA'
              }} 
            />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Need help?</div>
              <div style={{ fontSize: 11, color: '#BAE0FA' }}>We usually reply fast</div>
            </div>
          </div>
          <button
            onClick={toggleOpen}
            aria-label="Close"
            style={{
              width: 30, height: 30, borderRadius: '50%', border: 'none',
              background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
          <p style={{ margin: '0 0 14px', fontSize: '12.5px', color: '#6b7280' }}>
            Facing an issue on this page? Describe it and/or attach a
            screenshot — we'll send it straight to our support WhatsApp.
          </p>

          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What went wrong on this page?"
            rows={4}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #BAE0FA',
              fontSize: '13px',
              resize: 'vertical',
              marginBottom: '12px',
              fontFamily: 'inherit',
            }}
          />

          {!screenshotPreview ? (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
              <button
                onClick={captureScreenshot}
                disabled={capturing}
                style={{
                  flex: 1,
                  padding: '9px 6px',
                  borderRadius: '8px',
                  border: '1px solid #1E3A8A',
                  background: '#EFF6FF',
                  color: '#1E3A8A',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: capturing ? 'not-allowed' : 'pointer',
                }}
              >
                {capturing ? 'Capturing…' : '📷 Capture Page'}
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  flex: 1,
                  padding: '9px 6px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: '#f9fafb',
                  color: '#374151',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                📁 Upload Image
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleManualUpload}
                style={{ display: 'none' }}
              />
            </div>
          ) : (
            <div style={{ marginBottom: '14px', position: 'relative' }}>
              <img
                src={screenshotPreview}
                alt="Screenshot preview"
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  display: 'block',
                }}
              />
              <button
                onClick={removeScreenshot}
                style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  background: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  lineHeight: 1,
                }}
                aria-label="Remove screenshot"
              >
                ✕
              </button>
            </div>
          )}

          {statusMsg && (
            <p style={{ fontSize: '12px', color: '#1E3A8A', margin: '0 0 12px' }}>
              {statusMsg}
            </p>
          )}
        </div>

        <div style={{ padding: '16px 20px', borderTop: '1px solid #E5EEFB', flexShrink: 0 }}>
          <button
            onClick={handleSend}
            disabled={sending || (!description.trim() && !screenshotBlob)}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: 'none',
              background:
                sending || (!description.trim() && !screenshotBlob)
                  ? '#93C5FD'
                  : 'linear-gradient(135deg, #1E3A8A, #0B1F4D)',
              color: '#fff',
              fontSize: '13.5px',
              fontWeight: 700,
              cursor:
                sending || (!description.trim() && !screenshotBlob)
                  ? 'not-allowed'
                  : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            {sending ? 'Sending…' : '📲 Send via Web WhatsApp'}
          </button>
        </div>
      </div>
    </div>
  );
}